import React, { useState, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Info,
  Building2,
  Package,
  Users,
  Download,
  X,
  AlertTriangle,
  Trash2,
  TrendingUp,
  FileText,
  DollarSign,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DataImport = () => {
  const [activeTab, setActiveTab] = useState("suppliers");
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const fileInputRef = useRef(null);
  
  // Monthly sales specific state
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlySalesSummary, setMonthlySalesSummary] = useState(null);

  const importTypes = {
    suppliers: {
      label: "Suppliers",
      icon: Building2,
      endpoint: "/import/suppliers",
      templateEndpoint: "/import/suppliers/template",
      description: "Import supplier information including contact details, account numbers, and balances",
      supportsReplace: false,
      fileType: "excel",
      columns: [
        { name: "Supplier", required: true, description: "Supplier name" },
        { name: "Contact", required: false, description: "Contact person" },
        { name: "Tel1", required: false, description: "Phone number" },
        { name: "Email", required: false, description: "Email address" },
        { name: "Website", required: false, description: "Website URL" },
        { name: "AccountNo", required: false, description: "Your account with supplier" },
        { name: "CreditLimit", required: false, description: "Credit limit amount" },
        { name: "Notes", required: false, description: "Additional notes" },
        { name: "VAT NO", required: false, description: "VAT number" },
        { name: "Balance", required: false, description: "Opening balance" },
      ]
    },
    stock: {
      label: "Stock/Products",
      icon: Package,
      endpoint: "/import/stock",
      templateEndpoint: "/import/stock/template",
      description: "Import product information including pricing, stock levels, and categories",
      supportsReplace: true,
      fileType: "excel",
      columns: [
        { name: "Barcode", required: false, description: "Product barcode/EAN" },
        { name: "PLU", required: true, description: "Product code" },
        { name: "Description", required: true, description: "Product name" },
        { name: "SOH", required: false, description: "Stock on hand" },
        { name: "ReOrder Level", required: false, description: "Reorder level" },
        { name: "Department", required: false, description: "Department name" },
        { name: "Category", required: false, description: "Category name" },
        { name: "Supplier", required: false, description: "Supplier name" },
        { name: "SupPLU", required: false, description: "Supplier product code" },
        { name: "CostEx", required: false, description: "Cost price excl VAT" },
        { name: "CostIncl", required: false, description: "Cost price incl VAT" },
        { name: "Retail", required: false, description: "Selling price incl VAT" },
        { name: "MARK UP %", required: false, description: "Markup percentage" },
        { name: "Packsize", required: false, description: "Pack size" },
        { name: "DateLastSold", required: false, description: "Last sold date" },
        { name: "Discontinued", required: false, description: "Is discontinued (0/1)" },
      ]
    },
    customers: {
      label: "Customers",
      icon: Users,
      endpoint: "/import/customers",
      templateEndpoint: "/import/customers/template",
      description: "Import customer information including contact details, account numbers, balances, aging, and discount levels",
      supportsReplace: false,
      fileType: "excel",
      columns: [
        { name: "Customer/Name", required: true, description: "Customer name" },
        { name: "Contact", required: false, description: "Contact person" },
        { name: "Tel1", required: false, description: "Phone number" },
        { name: "Email", required: false, description: "Email address" },
        { name: "AccountNo", required: false, description: "Account number" },
        { name: "CreditLimit", required: false, description: "Credit limit" },
        { name: "Notes", required: false, description: "Additional notes" },
        { name: "VatExempt", required: false, description: "VAT exempt (Yes/No)" },
        { name: "VAT NO", required: false, description: "VAT number" },
        { name: "CKNo", required: false, description: "Company registration" },
        { name: "BillAdd1/Street", required: false, description: "Address line 1" },
        { name: "BillAdd2", required: false, description: "Address line 2" },
        { name: "BillAdd3", required: false, description: "Address line 3" },
        { name: "Postal1", required: false, description: "Postal code 1" },
        { name: "Postal2", required: false, description: "Postal code 2" },
        { name: "Postal3", required: false, description: "Postal code 3" },
        { name: "Balance", required: false, description: "Account balance" },
        { name: "Current", required: false, description: "Current owing" },
        { name: "Days30", required: false, description: "30 days aging" },
        { name: "Days60", required: false, description: "60 days aging" },
        { name: "Days90", required: false, description: "90 days aging" },
        { name: "Days120", required: false, description: "120 days aging" },
        { name: "LastPurchase", required: false, description: "Last purchase date" },
        { name: "LastPayment", required: false, description: "Last payment date" },
        { name: "Visits", required: false, description: "Visit count" },
        { name: "CustomerLevel", required: false, description: "Level (0/1/2)" },
      ],
      disabled: false
    },
    sales_history: {
      label: "Sales History",
      icon: TrendingUp,
      endpoint: "/import/sales-history",
      templateEndpoint: "/import/sales-history/template",
      description: "Import historical sales data from EasyAs PDF reports to power auto-order calculations",
      supportsReplace: true,
      fileType: "pdf",
      columns: [
        { name: "Date", required: false, description: "Sale date (YYYY-MM-DD)" },
        { name: "PLU", required: true, description: "Product code" },
        { name: "Description", required: false, description: "Product name" },
        { name: "User", required: false, description: "Cashier/User" },
        { name: "Account", required: false, description: "Account sale (YES/blank)" },
        { name: "Ref", required: false, description: "Reference number" },
        { name: "Name", required: false, description: "Customer name" },
        { name: "Contact", required: false, description: "Customer contact" },
        { name: "Qty", required: true, description: "Quantity sold" },
        { name: "Retail", required: false, description: "Retail price" },
        { name: "Profit", required: false, description: "Profit amount" },
        { name: "Total", required: false, description: "Total sale value" },
      ],
      disabled: false
    },
    daily_sales_pdf: {
      label: "Daily Sales (PDF)",
      icon: FileText,
      endpoint: "/import/daily-sales-pdf",
      templateEndpoint: "/import/daily-sales-pdf/preview",
      description: "Import daily sales from PDF report. Skips duplicates and notifies you of missing PLU codes.",
      supportsReplace: false,
      fileType: "pdf",
      columns: [
        { name: "Date", required: true, description: "Sale date (YYYY-MM-DD)" },
        { name: "PLU", required: true, description: "Product code" },
        { name: "Description", required: false, description: "Product name" },
        { name: "User", required: false, description: "Cashier/User" },
        { name: "Account", required: false, description: "Account sale (YES/blank)" },
        { name: "Ref", required: false, description: "Reference number" },
        { name: "Name", required: false, description: "Customer name" },
        { name: "Contact", required: false, description: "Customer contact" },
        { name: "Qty", required: true, description: "Quantity sold" },
        { name: "Retail", required: true, description: "Retail price" },
        { name: "Profit", required: false, description: "Profit amount" },
        { name: "Total", required: true, description: "Total sale value" },
      ],
      disabled: false
    },
    prices: {
      label: "Price Update",
      icon: DollarSign,
      endpoint: "/import/prices",
      templateEndpoint: "/import/prices/template",
      description: "Bulk update selling prices by PLU code. Prices of R0.00 will be skipped.",
      supportsReplace: false,
      fileType: "excel",
      columns: [
        { name: "PLU", required: true, description: "Product code to match" },
        { name: "Description", required: false, description: "For reference only" },
        { name: "RETAIL (STD)", required: true, description: "New selling price incl VAT" },
      ],
      disabled: false
    },
    vat_zero_rated: {
      label: "VAT Zero-Rated",
      icon: FileSpreadsheet,
      endpoint: "/import/vat-zero-rated",
      templateEndpoint: "/import/vat-zero-rated/preview",
      description: "Mark products as 0% VAT (zero-rated) based on PLU codes. Upload an Excel file with PLU codes in column A.",
      supportsReplace: false,
      fileType: "excel",
      columns: [
        { name: "PLU", required: true, description: "Product code (Column A)" },
        { name: "Description", required: false, description: "For reference only (Column B)" },
        { name: "SOH", required: false, description: "For reference only (Column C)" },
      ],
      disabled: false
    },
    monthly_sales: {
      label: "Monthly Sales",
      icon: TrendingUp,
      endpoint: "/import/monthly-sales",
      templateEndpoint: null,
      description: "Import monthly sales by period (Jan 2025, Feb 2025, etc.) from EasyAs PDF reports. Track sales history month by month.",
      supportsReplace: true,
      fileType: "pdf",
      requiresMonthYear: true,
      columns: [
        { name: "Date", required: false, description: "Sale date (YYYY-MM-DD)" },
        { name: "PLU", required: true, description: "Product code" },
        { name: "Description", required: false, description: "Product name" },
        { name: "Qty", required: true, description: "Quantity sold" },
        { name: "Total", required: false, description: "Total sale value" },
        { name: "Profit", required: false, description: "Profit amount" },
      ],
      disabled: false
    }
  };

  // Fetch monthly sales summary when tab changes to monthly_sales
  const fetchMonthlySalesSummary = async () => {
    try {
      const response = await axios.get(`${API}/monthly-sales/summary`);
      setMonthlySalesSummary(response.data);
    } catch (error) {
      console.error("Error fetching monthly sales summary:", error);
    }
  };

  // Load monthly sales summary when tab is active
  React.useEffect(() => {
    if (activeTab === 'monthly_sales') {
      fetchMonthlySalesSummary();
    }
  }, [activeTab]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    const importType = importTypes[activeTab];
    if (file) {
      const isPdf = importType.fileType === 'pdf';
      const validExtensions = isPdf ? ['.pdf'] : ['.xlsx', '.xls'];
      const isValidFile = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      
      if (!isValidFile) {
        toast.error(isPdf ? "Please select a PDF file (.pdf)" : "Please select an Excel file (.xlsx or .xls)");
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    const importType = importTypes[activeTab];
    if (importType.disabled) {
      toast.error("This import type is not yet available");
      return;
    }

    // Validate month/year for monthly sales
    if (importType.requiresMonthYear) {
      if (!selectedMonth || !selectedYear) {
        toast.error("Please select both month and year");
        return;
      }
    }

    // Confirm if replace all is selected
    if (replaceAll && importType.supportsReplace) {
      const monthNames = ["", "January", "February", "March", "April", "May", "June", 
                         "July", "August", "September", "October", "November", "December"];
      const message = activeTab === 'monthly_sales'
        ? `WARNING: This will DELETE ALL existing sales data for ${monthNames[selectedMonth]} ${selectedYear} and replace it with the imported data. Are you sure you want to continue?`
        : activeTab === 'sales_history'
        ? "WARNING: This will DELETE ALL existing sales history and replace them with the imported data. Are you sure you want to continue?"
        : "WARNING: This will DELETE ALL existing products and replace them with the imported data. Are you sure you want to continue?";
      if (!window.confirm(message)) {
        return;
      }
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      // Build URL with parameters
      let url = `${API}${importType.endpoint}`;
      const params = new URLSearchParams();
      
      if (replaceAll && importType.supportsReplace) {
        params.append(activeTab === 'monthly_sales' ? "replace_existing" : "replace_all", "true");
      }
      
      if (importType.requiresMonthYear) {
        params.append("year", selectedYear);
        params.append("month", selectedMonth);
      }
      
      if (params.toString()) {
        url += "?" + params.toString();
      }

      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 300000, // 5 minutes timeout for large PDF files
      });

      setImportResult(response.data);
      
      const deleted = response.data.deleted_count || 0;
      const created = response.data.created_count || response.data.unique_plu_count || 0;
      const updated = response.data.updated_count || 0;
      const period = response.data.period || '';
      
      if (activeTab === 'monthly_sales') {
        toast.success(`Monthly sales imported for ${period}! ${created} products with sales data.`);
        // Refresh the monthly sales summary
        fetchMonthlySalesSummary();
      } else if (deleted > 0) {
        toast.success(`Import complete! Deleted: ${deleted}, Created: ${created}`);
      } else {
        toast.success(`Import complete! Created: ${created}, Updated: ${updated}`);
      }
    } catch (error) {
      console.error("Error importing:", error);
      console.error("Error response:", error.response?.data);
      const errorDetail = error.response?.data?.detail;
      if (errorDetail) {
        // Show specific error with longer duration for important messages
        if (errorDetail.includes("already exists")) {
          toast.error(`${errorDetail}\n\nCheck the "Replace existing data" checkbox if you want to overwrite.`, { 
            duration: 10000 
          });
        } else {
          toast.error(errorDetail, { duration: 8000 });
        }
      } else if (error.response?.status === 500) {
        const serverError = error.response?.data?.detail || "Unknown server error";
        toast.error(`Server error: ${serverError}`, { duration: 10000 });
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error("Import timed out. The file may be too large. Try splitting it into smaller files.", { duration: 10000 });
      } else {
        toast.error(`Failed to import: ${error.message || "Unknown error"}. Please check the file format.`, { duration: 8000 });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    const importType = importTypes[activeTab];
    if (file) {
      const isPdf = importType.fileType === 'pdf';
      const validExtensions = isPdf ? ['.pdf'] : ['.xlsx', '.xls'];
      const isValidFile = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      
      if (!isValidFile) {
        toast.error(isPdf ? "Please drop a PDF file (.pdf)" : "Please drop an Excel file (.xlsx or .xls)");
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const loadTemplate = async () => {
    const importType = importTypes[activeTab];
    if (importType.templateEndpoint) {
      try {
        const response = await axios.get(`${API}${importType.templateEndpoint}`);
        setTemplateInfo(response.data);
      } catch (error) {
        console.error("Error loading template:", error);
      }
    }
  };

  const currentImportType = importTypes[activeTab];
  const Icon = currentImportType.icon;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Upload className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-3xl font-bold">Data Import</h1>
          <p className="text-gray-500">Import data from Excel files</p>
        </div>
      </div>

      {/* Import Type Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedFile(null); setImportResult(null); setReplaceAll(false); }}>
        <TabsList className="mb-6">
          {Object.entries(importTypes).map(([key, type]) => {
            const TabIcon = type.icon;
            return (
              <TabsTrigger 
                key={key} 
                value={key} 
                disabled={type.disabled}
                className="flex items-center gap-2"
              >
                <TabIcon className="h-4 w-4" />
                {type.label}
                {type.disabled && <Badge variant="secondary" className="ml-1 text-xs">Soon</Badge>}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.entries(importTypes).map(([key, type]) => (
          <TabsContent key={key} value={key}>
            <div className="grid grid-cols-3 gap-6">
              {/* Left: Upload Section */}
              <div className="col-span-2 space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Icon className="h-5 w-5" />
                      Import {type.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{type.description}</p>
                    
                    {/* Month/Year Selection for Monthly Sales */}
                    {type.requiresMonthYear && (
                      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <Calendar className="h-5 w-5 text-blue-600" />
                          <Label className="text-blue-800 font-medium">Select Sales Period</Label>
                        </div>
                        <div className="flex gap-4">
                          <div className="flex-1">
                            <Label className="text-sm text-blue-700 mb-1 block">Month</Label>
                            <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                              <SelectTrigger className="bg-white">
                                <SelectValue placeholder="Select month" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">January</SelectItem>
                                <SelectItem value="2">February</SelectItem>
                                <SelectItem value="3">March</SelectItem>
                                <SelectItem value="4">April</SelectItem>
                                <SelectItem value="5">May</SelectItem>
                                <SelectItem value="6">June</SelectItem>
                                <SelectItem value="7">July</SelectItem>
                                <SelectItem value="8">August</SelectItem>
                                <SelectItem value="9">September</SelectItem>
                                <SelectItem value="10">October</SelectItem>
                                <SelectItem value="11">November</SelectItem>
                                <SelectItem value="12">December</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex-1">
                            <Label className="text-sm text-blue-700 mb-1 block">Year</Label>
                            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                              <SelectTrigger className="bg-white">
                                <SelectValue placeholder="Select year" />
                              </SelectTrigger>
                              <SelectContent>
                                {[2020, 2021, 2022, 2023, 2024, 2025, 2026].map(year => (
                                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <p className="text-xs text-blue-600 mt-2">
                          Sales from the PDF will be stored under this period. You can import each month separately.
                        </p>
                      </div>
                    )}
                    
                    {/* Replace All Option */}
                    {type.supportsReplace && (
                      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            id="replace_all"
                            checked={replaceAll}
                            onCheckedChange={(checked) => setReplaceAll(checked)}
                          />
                          <div className="flex-1">
                            <Label htmlFor="replace_all" className="text-red-700 font-medium flex items-center gap-2 cursor-pointer">
                              <Trash2 className="h-4 w-4" />
                              {activeTab === 'monthly_sales'
                                ? `Replace existing data for ${["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][selectedMonth]} ${selectedYear}`
                                : activeTab === 'sales_history' 
                                ? 'Delete all existing sales history and replace with imported data'
                                : 'Delete all existing products and replace with imported data'
                              }
                            </Label>
                            <p className="text-sm text-red-600 mt-1">
                              {activeTab === 'monthly_sales'
                                ? `Warning: This will delete existing data for the selected month before importing!`
                                : activeTab === 'sales_history'
                                ? 'Warning: This will permanently delete ALL existing sales history before importing!'
                                : 'Warning: This will permanently delete ALL existing products before importing!'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Drop Zone */}
                    <div
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                        selectedFile
                          ? "border-green-300 bg-green-50"
                          : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={type.fileType === 'pdf' ? '.pdf' : '.xlsx,.xls'}
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      
                      {selectedFile ? (
                        <div className="flex items-center justify-center gap-3">
                          {type.fileType === 'pdf' ? (
                            <FileText className="h-12 w-12 text-green-600" />
                          ) : (
                            <FileSpreadsheet className="h-12 w-12 text-green-600" />
                          )}
                          <div className="text-left">
                            <p className="font-medium text-green-700">{selectedFile.name}</p>
                            <p className="text-sm text-green-600">
                              {(selectedFile.size / 1024).toFixed(1)} KB - Ready to import
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFile(null);
                              setImportResult(null);
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          {type.fileType === 'pdf' ? (
                            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                          ) : (
                            <FileSpreadsheet className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                          )}
                          <p className="text-gray-600 font-medium">
                            Drop your {type.fileType === 'pdf' ? 'PDF' : 'Excel'} file here or click to browse
                          </p>
                          <p className="text-sm text-gray-400 mt-1">
                            {type.fileType === 'pdf' ? 'Supports .pdf files' : 'Supports .xlsx and .xls files'}
                          </p>
                        </>
                      )}
                    </div>

                    {/* Upload Button */}
                    <Button
                      onClick={handleUpload}
                      disabled={!selectedFile || uploading || type.disabled}
                      className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
                      size="lg"
                    >
                      {uploading ? (
                        <>
                          <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="h-5 w-5 mr-2" />
                          Import {type.label}
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>

                {/* Import Result */}
                {importResult && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        {importResult.error_count === 0 ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-amber-600" />
                        )}
                        Import Results
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {/* Summary - Sales History specific */}
                      {activeTab === 'sales_history' ? (
                        <div className="space-y-4 mb-4">
                          <div className="grid grid-cols-4 gap-4">
                            {importResult.deleted_count > 0 && (
                              <div className="bg-red-100 p-3 rounded-lg text-center">
                                <div className="text-2xl font-bold text-red-700">{importResult.deleted_count}</div>
                                <div className="text-sm text-red-800">Previous Deleted</div>
                              </div>
                            )}
                            <div className="bg-blue-50 p-3 rounded-lg text-center">
                              <div className="text-2xl font-bold text-blue-600">{importResult.total_rows_processed || 0}</div>
                              <div className="text-sm text-blue-700">Rows Processed</div>
                            </div>
                            <div className="bg-green-50 p-3 rounded-lg text-center">
                              <div className="text-2xl font-bold text-green-600">{importResult.matched_to_products || importResult.imported_sales_records || 0}</div>
                              <div className="text-sm text-green-700">Sales Imported</div>
                            </div>
                            <div className="bg-purple-50 p-3 rounded-lg text-center">
                              <div className="text-2xl font-bold text-purple-600">{importResult.unique_products || importResult.unique_plu_count || 0}</div>
                              <div className="text-sm text-purple-700">Unique PLUs</div>
                            </div>
                          </div>
                          
                          {/* Skipped info */}
                          <div className="grid grid-cols-3 gap-4">
                            <div className="bg-gray-50 p-3 rounded-lg text-center">
                              <div className="text-xl font-bold text-gray-600">{importResult.skipped_count || importResult.skipped_no_plu || 0}</div>
                              <div className="text-sm text-gray-700">Skipped (No PLU)</div>
                            </div>
                            <div className="bg-amber-50 p-3 rounded-lg text-center">
                              <div className="text-xl font-bold text-amber-600">{importResult.allocated_to_unallocated || importResult.skipped_plu_not_found || 0}</div>
                              <div className="text-sm text-amber-700">Skipped (PLU Not Found)</div>
                            </div>
                            <div className="bg-red-50 p-3 rounded-lg text-center">
                              <div className="text-xl font-bold text-red-600">{importResult.error_count || 0}</div>
                              <div className="text-sm text-red-700">Errors</div>
                            </div>
                          </div>
                          
                          {/* Skipped PLU list */}
                          {(importResult.skipped_plu_list || importResult.unallocated_plu_samples) && (importResult.skipped_plu_list?.length > 0 || importResult.unallocated_plu_samples?.length > 0) && (
                            <Alert className="bg-amber-50 border-amber-200">
                              <AlertTriangle className="h-4 w-4 text-amber-600" />
                              <AlertTitle className="text-amber-800">PLU Codes Not Found in Products</AlertTitle>
                              <AlertDescription className="text-amber-700">
                                <p className="text-sm mb-2">These PLU codes were in your sales data but don't exist in the product database:</p>
                                <div className="flex flex-wrap gap-1 max-h-24 overflow-auto">
                                  {(importResult.skipped_plu_list || importResult.unallocated_plu_samples || []).map((plu, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs bg-white">
                                      {plu}
                                    </Badge>
                                  ))}
                                </div>
                                {(importResult.allocated_to_unallocated || importResult.skipped_plu_not_found || 0) > (importResult.skipped_plu_list || importResult.unallocated_plu_samples || []).length && (
                                  <p className="text-xs mt-2">...and {(importResult.allocated_to_unallocated || importResult.skipped_plu_not_found) - (importResult.skipped_plu_list || importResult.unallocated_plu_samples || []).length} more</p>
                                )}
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      ) : activeTab === 'monthly_sales' ? (
                        /* Monthly Sales Results */
                        <div className="space-y-4 mb-4">
                          <div className="p-3 bg-blue-50 rounded-lg text-center mb-4">
                            <div className="text-lg font-bold text-blue-800">{importResult.period || 'Unknown Period'}</div>
                            <div className="text-sm text-blue-600">Sales Period Imported</div>
                          </div>
                          <div className="grid grid-cols-4 gap-4">
                            {importResult.deleted_count > 0 && (
                              <div className="bg-red-100 p-3 rounded-lg text-center">
                                <div className="text-2xl font-bold text-red-700">{importResult.deleted_count}</div>
                                <div className="text-sm text-red-800">Previous Deleted</div>
                              </div>
                            )}
                            <div className="bg-blue-50 p-3 rounded-lg text-center">
                              <div className="text-2xl font-bold text-blue-600">{importResult.total_rows_processed || 0}</div>
                              <div className="text-sm text-blue-700">Rows Processed</div>
                            </div>
                            <div className="bg-green-50 p-3 rounded-lg text-center">
                              <div className="text-2xl font-bold text-green-600">{importResult.matched_to_products || importResult.imported_sales_records || 0}</div>
                              <div className="text-sm text-green-700">Sales Records</div>
                            </div>
                            <div className="bg-purple-50 p-3 rounded-lg text-center">
                              <div className="text-2xl font-bold text-purple-600">{importResult.unique_products || importResult.unique_plu_count || 0}</div>
                              <div className="text-sm text-purple-700">Products with Sales</div>
                            </div>
                          </div>
                          
                          {/* Skipped info */}
                          <div className="grid grid-cols-3 gap-4">
                            <div className="bg-gray-50 p-3 rounded-lg text-center">
                              <div className="text-xl font-bold text-gray-600">{importResult.skipped_count || importResult.skipped_no_plu || 0}</div>
                              <div className="text-sm text-gray-700">Skipped (No PLU)</div>
                            </div>
                            <div className="bg-amber-50 p-3 rounded-lg text-center">
                              <div className="text-xl font-bold text-amber-600">{importResult.allocated_to_unallocated || importResult.skipped_plu_not_found || 0}</div>
                              <div className="text-sm text-amber-700">Skipped (PLU Not Found)</div>
                            </div>
                            <div className="bg-red-50 p-3 rounded-lg text-center">
                              <div className="text-xl font-bold text-red-600">{importResult.error_count || 0}</div>
                              <div className="text-sm text-red-700">Errors</div>
                            </div>
                          </div>
                          
                          {/* Skipped PLU list */}
                          {(importResult.skipped_plu_list || importResult.unallocated_plu_samples) && (importResult.skipped_plu_list?.length > 0 || importResult.unallocated_plu_samples?.length > 0) && (
                            <Alert className="bg-amber-50 border-amber-200">
                              <AlertTriangle className="h-4 w-4 text-amber-600" />
                              <AlertTitle className="text-amber-800">PLU Codes Not Found in Products</AlertTitle>
                              <AlertDescription className="text-amber-700">
                                <p className="text-sm mb-2">These PLU codes were in your sales data but don't exist in the product database:</p>
                                <div className="flex flex-wrap gap-1 max-h-24 overflow-auto">
                                  {(importResult.skipped_plu_list || importResult.unallocated_plu_samples || []).map((plu, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs bg-white">
                                      {plu}
                                    </Badge>
                                  ))}
                                </div>
                                {(importResult.allocated_to_unallocated || importResult.skipped_plu_not_found || 0) > (importResult.skipped_plu_list || importResult.unallocated_plu_samples || []).length && (
                                  <p className="text-xs mt-2">...and {(importResult.allocated_to_unallocated || importResult.skipped_plu_not_found) - (importResult.skipped_plu_list || importResult.unallocated_plu_samples || []).length} more</p>
                                )}
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      ) : activeTab === 'daily_sales_pdf' ? (
                        /* Daily Sales PDF Results */
                        <div className="space-y-4 mb-4">
                          {/* Success message */}
                          <Alert className={importResult.missing_plus_count > 0 ? "bg-amber-50 border-amber-300" : "bg-green-50 border-green-300"}>
                            <CheckCircle className={`h-5 w-5 ${importResult.missing_plus_count > 0 ? 'text-amber-600' : 'text-green-600'}`} />
                            <AlertTitle className={importResult.missing_plus_count > 0 ? 'text-amber-800' : 'text-green-800'}>
                              {importResult.message}
                            </AlertTitle>
                          </Alert>
                          
                          <div className="grid grid-cols-3 gap-4">
                            <div className="bg-green-50 p-4 rounded-lg text-center">
                              <div className="text-3xl font-bold text-green-600">{importResult.imported_count || 0}</div>
                              <div className="text-sm text-green-700">Items Imported</div>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg text-center">
                              <div className="text-3xl font-bold text-gray-600">{importResult.skipped_count || 0}</div>
                              <div className="text-sm text-gray-700">Duplicates Skipped</div>
                            </div>
                            <div className="bg-blue-50 p-4 rounded-lg text-center">
                              <div className="text-3xl font-bold text-blue-600">{importResult.dates_processed?.length || 0}</div>
                              <div className="text-sm text-blue-700">Days Processed</div>
                            </div>
                          </div>
                          
                          {/* Missing PLU codes - PROMINENT WARNING */}
                          {importResult.missing_plus && importResult.missing_plus.length > 0 && (
                            <Alert className="bg-red-50 border-red-400 border-2">
                              <AlertTriangle className="h-5 w-5 text-red-600" />
                              <AlertTitle className="text-red-800 text-lg font-bold">
                                ⚠️ {importResult.missing_plus_count} PLU Codes NOT in System!
                              </AlertTitle>
                              <AlertDescription className="text-red-700">
                                <p className="mb-3">The following PLU codes from your PDF are not in the product database. These sales were imported but won&apos;t show correct product details:</p>
                                <div className="bg-white rounded border border-red-200 p-3 max-h-48 overflow-auto">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b">
                                        <th className="text-left py-1 px-2 font-semibold">PLU Code</th>
                                        <th className="text-left py-1 px-2 font-semibold">Description from PDF</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {importResult.missing_plus.slice(0, 50).map((item, idx) => (
                                        <tr key={idx} className="border-b hover:bg-red-50">
                                          <td className="py-1 px-2 font-mono font-bold">{item.plu}</td>
                                          <td className="py-1 px-2">{item.description}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {importResult.missing_plus_count > 50 && (
                                    <p className="text-xs mt-2 text-center">...and {importResult.missing_plus_count - 50} more</p>
                                  )}
                                </div>
                                <p className="mt-3 text-sm font-medium">
                                  👆 Please add these products to the Stock/Products database and then re-import for accurate reporting.
                                </p>
                              </AlertDescription>
                            </Alert>
                          )}
                          
                          {/* Dates processed */}
                          {importResult.dates_processed && importResult.dates_processed.length > 0 && (
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <p className="text-sm font-semibold text-blue-800 mb-2">Dates in PDF:</p>
                              <div className="flex flex-wrap gap-2">
                                {importResult.dates_processed.sort().map((date, idx) => (
                                  <Badge key={idx} className="bg-blue-100 text-blue-800">{date}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : activeTab === 'prices' ? (
                        /* Price Update Results */
                        <div className="space-y-4 mb-4">
                          <div className="grid grid-cols-4 gap-4">
                            <div className="bg-blue-50 p-3 rounded-lg text-center">
                              <div className="text-2xl font-bold text-blue-600">{importResult.total_rows_processed || 0}</div>
                              <div className="text-sm text-blue-700">Rows Processed</div>
                            </div>
                            <div className="bg-green-50 p-3 rounded-lg text-center">
                              <div className="text-2xl font-bold text-green-600">{importResult.updated_count || 0}</div>
                              <div className="text-sm text-green-700">Prices Updated</div>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg text-center">
                              <div className="text-xl font-bold text-gray-600">{importResult.skipped_zero_price || 0}</div>
                              <div className="text-sm text-gray-700">Skipped (R0.00)</div>
                            </div>
                            <div className="bg-amber-50 p-3 rounded-lg text-center">
                              <div className="text-xl font-bold text-amber-600">{importResult.skipped_not_found || 0}</div>
                              <div className="text-sm text-amber-700">PLU Not Found</div>
                            </div>
                          </div>
                          
                          {/* Not found PLU list */}
                          {importResult.not_found_plu_list && importResult.not_found_plu_list.length > 0 && (
                            <Alert className="bg-amber-50 border-amber-200">
                              <AlertTriangle className="h-4 w-4 text-amber-600" />
                              <AlertTitle className="text-amber-800">PLU Codes Not Found</AlertTitle>
                              <AlertDescription className="text-amber-700">
                                <p className="text-sm mb-2">These PLU codes don't exist in the product database:</p>
                                <div className="flex flex-wrap gap-1 max-h-24 overflow-auto">
                                  {importResult.not_found_plu_list.map((plu, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs bg-white">
                                      {plu}
                                    </Badge>
                                  ))}
                                </div>
                                {importResult.skipped_not_found > importResult.not_found_plu_list.length && (
                                  <p className="text-xs mt-2">...and {importResult.skipped_not_found - importResult.not_found_plu_list.length} more</p>
                                )}
                              </AlertDescription>
                            </Alert>
                          )}
                          
                          {importResult.error_count > 0 && (
                            <div className="bg-red-50 p-3 rounded-lg text-center">
                              <div className="text-xl font-bold text-red-600">{importResult.error_count}</div>
                              <div className="text-sm text-red-700">Errors</div>
                            </div>
                          )}
                        </div>
                      ) : activeTab === 'vat_zero_rated' ? (
                        /* VAT Zero-Rated Import Results */
                        <div className="space-y-4 mb-4">
                          <div className="grid grid-cols-4 gap-4">
                            <div className="bg-blue-50 p-3 rounded-lg text-center">
                              <div className="text-2xl font-bold text-blue-600">{importResult.summary?.total_in_file || 0}</div>
                              <div className="text-sm text-blue-700">PLUs in File</div>
                            </div>
                            <div className="bg-green-50 p-3 rounded-lg text-center">
                              <div className="text-2xl font-bold text-green-600">{importResult.updated_count || 0}</div>
                              <div className="text-sm text-green-700">Updated to 0% VAT</div>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg text-center">
                              <div className="text-xl font-bold text-gray-600">{importResult.skipped_count || 0}</div>
                              <div className="text-sm text-gray-700">Already 0% VAT</div>
                            </div>
                            <div className="bg-amber-50 p-3 rounded-lg text-center">
                              <div className="text-xl font-bold text-amber-600">{importResult.error_count || 0}</div>
                              <div className="text-sm text-amber-700">PLU Not Found</div>
                            </div>
                          </div>
                          
                          {/* Updated products list */}
                          {importResult.updated_products && importResult.updated_products.length > 0 && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                              <p className="text-sm font-semibold text-green-800 mb-2">Products Updated to 0% VAT:</p>
                              <div className="max-h-48 overflow-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-green-200">
                                      <th className="text-left py-1 px-2">PLU</th>
                                      <th className="text-left py-1 px-2">Product Name</th>
                                      <th className="text-right py-1 px-2">Previous VAT</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {importResult.updated_products.map((p, idx) => (
                                      <tr key={idx} className="border-b border-green-100">
                                        <td className="py-1 px-2 font-mono">{p.plu_code}</td>
                                        <td className="py-1 px-2">{p.name}</td>
                                        <td className="py-1 px-2 text-right">{p.previous_vat_rate}%</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                          
                          {/* Not found PLU list */}
                          {importResult.not_found_plus && importResult.not_found_plus.length > 0 && (
                            <Alert className="bg-amber-50 border-amber-200">
                              <AlertTriangle className="h-4 w-4 text-amber-600" />
                              <AlertTitle className="text-amber-800">PLU Codes Not Found</AlertTitle>
                              <AlertDescription className="text-amber-700">
                                <p className="text-sm mb-2">These PLU codes don&apos;t exist in the product database:</p>
                                <div className="flex flex-wrap gap-1 max-h-24 overflow-auto">
                                  {importResult.not_found_plus.map((plu, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs bg-white">
                                      {plu}
                                    </Badge>
                                  ))}
                                </div>
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      ) : (
                        /* Standard Summary for other import types */
                        <div className={`grid ${importResult.deleted_count > 0 ? 'grid-cols-5' : 'grid-cols-4'} gap-4 mb-4`}>
                          {importResult.deleted_count > 0 && (
                            <div className="bg-red-100 p-3 rounded-lg text-center">
                              <div className="text-2xl font-bold text-red-700">{importResult.deleted_count}</div>
                              <div className="text-sm text-red-800">Deleted</div>
                            </div>
                          )}
                          <div className="bg-green-50 p-3 rounded-lg text-center">
                            <div className="text-2xl font-bold text-green-600">{importResult.created_count || 0}</div>
                            <div className="text-sm text-green-700">Created</div>
                          </div>
                          <div className="bg-blue-50 p-3 rounded-lg text-center">
                            <div className="text-2xl font-bold text-blue-600">{importResult.updated_count || 0}</div>
                            <div className="text-sm text-blue-700">Updated</div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg text-center">
                            <div className="text-2xl font-bold text-gray-600">{importResult.skipped_count || 0}</div>
                            <div className="text-sm text-gray-700">Skipped</div>
                          </div>
                          <div className="bg-red-50 p-3 rounded-lg text-center">
                            <div className="text-2xl font-bold text-red-600">{importResult.error_count || 0}</div>
                            <div className="text-sm text-red-700">Errors</div>
                          </div>
                        </div>
                      )}

                      {/* Errors */}
                      {importResult.errors && importResult.errors.length > 0 && (
                        <Alert variant="destructive" className="mb-4">
                          <AlertCircle className="h-4 w-4" />
                          <AlertTitle>Import Errors</AlertTitle>
                          <AlertDescription>
                            <ul className="list-disc ml-4 mt-2 text-sm">
                              {importResult.errors.slice(0, 10).map((error, idx) => (
                                <li key={idx}>{error}</li>
                              ))}
                              {importResult.errors.length > 10 && (
                                <li>...and {importResult.errors.length - 10} more errors</li>
                              )}
                            </ul>
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Details */}
                      {importResult.details && importResult.details.length > 0 && (
                        <div className="max-h-60 overflow-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Action</TableHead>
                                <TableHead>Reference</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {importResult.details.slice(0, 20).map((detail, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="font-medium">
                                    {detail.name || detail.plu_code}
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant={detail.action === 'created' ? 'default' : 'secondary'}>
                                      {detail.action}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="font-mono text-sm">
                                    {detail.supplier_number || detail.plu_code || '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          {importResult.details.length > 20 && (
                            <p className="text-sm text-gray-500 mt-2 text-center">
                              Showing first 20 of {importResult.details.length} items
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Right: Column Info */}
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Info className="h-4 w-4" />
                      Expected Columns
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500 mb-3">
                      Your Excel file should have these columns:
                    </p>
                    <div className="space-y-2">
                      {type.columns.map((col, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <div className={`w-2 h-2 rounded-full mt-1.5 ${col.required ? 'bg-red-500' : 'bg-gray-300'}`}></div>
                          <div>
                            <span className="font-medium">{col.name}</span>
                            {col.required && <span className="text-red-500 text-xs ml-1">*</span>}
                            <p className="text-gray-500 text-xs">{col.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-4">
                      <span className="text-red-500">*</span> = Required field
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Tips</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {type.fileType === 'pdf' ? (
                      <ul className="text-sm text-gray-600 space-y-2">
                        <li>• File must be a PDF exported from EasyAs</li>
                        <li>• Report should contain item sales data table</li>
                        <li>• PLU codes not in product database are skipped</li>
                        <li>• Same PLU appearing multiple times will be aggregated</li>
                        <li>• Sales history powers the auto-order feature</li>
                        <li>• Use "Replace All" to clear existing history first</li>
                      </ul>
                    ) : key === 'monthly_sales' ? (
                      <ul className="text-sm text-gray-600 space-y-2">
                        <li>• Select the month and year before uploading</li>
                        <li>• Sales are aggregated by PLU for the selected period</li>
                        <li>• Import each month separately to track trends</li>
                        <li>• Use "Replace" to update data for a specific month</li>
                        <li>• View imported months in the summary below</li>
                      </ul>
                    ) : (
                      <ul className="text-sm text-gray-600 space-y-2">
                        <li>• First row should contain column headers</li>
                        <li>• Existing records (matched by name) will be updated</li>
                        <li>• New records will be created automatically</li>
                        <li>• Currency values can include 'R' and commas</li>
                        <li>• Empty rows will be skipped</li>
                      </ul>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
            
            {/* Monthly Sales Summary - Only for monthly_sales tab */}
            {key === 'monthly_sales' && monthlySalesSummary && monthlySalesSummary.periods?.length > 0 && (
              <Card className="mt-6 border-green-200">
                <CardHeader className="bg-green-50 border-b">
                  <CardTitle className="flex items-center gap-2 text-green-800">
                    <CheckCircle className="h-5 w-5" />
                    Imported Monthly Sales Periods ({monthlySalesSummary.total_periods})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Period</TableHead>
                        <TableHead className="text-right">Products</TableHead>
                        <TableHead className="text-right">Qty Sold</TableHead>
                        <TableHead className="text-right">Total Value</TableHead>
                        <TableHead className="text-right">Profit</TableHead>
                        <TableHead className="text-right">Transactions</TableHead>
                        <TableHead>Imported</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthlySalesSummary.periods.map((period) => (
                        <TableRow key={period.period_key}>
                          <TableCell className="font-medium">{period.period_label}</TableCell>
                          <TableCell className="text-right">{period.total_products?.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{period.total_qty_sold?.toLocaleString()}</TableCell>
                          <TableCell className="text-right">R {period.total_value?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                          <TableCell className="text-right text-green-600">R {period.total_profit?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                          <TableCell className="text-right">{period.total_transactions?.toLocaleString()}</TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {new Date(period.imported_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={async () => {
                                if (window.confirm(`Delete all sales data for ${period.period_label}?`)) {
                                  try {
                                    await axios.delete(`${API}/monthly-sales/${period.period_key}`);
                                    toast.success(`Deleted data for ${period.period_label}`);
                                    fetchMonthlySalesSummary();
                                  } catch (error) {
                                    toast.error("Failed to delete period data");
                                  }
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default DataImport;
